import classNames from "classnames";
import { ButtonVariant, CoButton } from "components/CoButton";
import { Separator } from "components/Separator";
import Tooltip from "components/Tooltip";
import { ThreadInteractionStatus } from "helpers/api";
import { getTimeAgoLabel } from "helpers/date";
import pluralize from "helpers/pluralize";
import useLabels from "hooks/useLabels";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import {
  HistoryItem as HistoryItemType,
  HistoryType,
  ThreadHistoryItem,
} from "../types";
import HistoryActionsButton from "./HistoryActionsButton";
import {
  NavigationTabVariant,
  SidebarNavigationItem,
} from "./SidebarNavigation";

export enum HistoryItemVariant {
  Compact = "compact",
  Full = "full",
}

type HistoryItemProps = {
  history: HistoryItemType;
  onClick: (history: HistoryItemType) => void;
  onDelete: (history: HistoryItemType) => void;
  onSave: (history: HistoryItemType) => void;
  variant?: HistoryItemVariant;
};

export const isUploadedPaperUrl = (url: string) => {
  return url.includes("papers/uploaded/") || url.includes("papers/u/");
};

function HistoryItem({
  history,
  onClick,
  onDelete,
  onSave,
  variant = HistoryItemVariant.Compact,
}: HistoryItemProps) {
  const router = useRouter();
  const [isSelected, setIsSelected] = useState(false);
  const [bookmarkLabels, actionLabels] = useLabels(
    "features.bookmark-lists.bookmark-list-detail",
    "screens.sidebar.history"
  );

  const handleOnClick = () => {
    onClick(history);
  };

  useEffect(() => {
    if (router.asPath === history.link) {
      setIsSelected(true);
    } else {
      setIsSelected(false);
    }
  }, [router, history.link]);

  const isUploadedPaperItem = history.type === HistoryType.UploadedPaper;

  const id =
    history.type === HistoryType.Thread ? history.thread_id : history.id;
  const title =
    history.type === HistoryType.Thread ? history.title : history.query;

  // Use metadata from ThreadHistory
  const previewText =
    history.type === HistoryType.Thread
      ? (history as ThreadHistoryItem).latest_interaction_preview || ""
      : "";
  const interactionCount =
    history.type === HistoryType.Thread
      ? (history as ThreadHistoryItem).interaction_count || 1
      : 1;

  if (variant === HistoryItemVariant.Full) {
    return (
      <div className="border-b first:border-t border-border-base pt-4 pb-1.5 relative group @container isolate">
        <div className="relative z-10">
          <Link href={history.link} passHref legacyBehavior>
            <a onClick={handleOnClick}>
              <h3 className="base-md">{title}</h3>
              {history.type === HistoryType.Thread && (
                <>
                  {previewText ? (
                    <p className="text-fg-muted text-sm line-clamp-2 mt-2">
                      {previewText}
                    </p>
                  ) : null}
                </>
              )}
            </a>
          </Link>
          <div className="flex items-center justify-between min-h-9 mt-2">
            <div className="flex flex-1 items-center gap-1.5 tiny-normal text-fg-muted">
              {history.type === HistoryType.Thread && (
                <>
                  <span className="text-fg-base tiny-md">
                    {getTimeAgoLabel(history.created_at, true)}
                  </span>
                  <Separator />
                </>
              )}
              <span className="text-fg-base tiny-mono-bold mt-px">
                {interactionCount}
              </span>
              {pluralize(bookmarkLabels["queries"], {
                QUERY_COUNT: interactionCount,
              })}
            </div>
            <div className="flex justify-end opacity-0 transition-opacity ease-in-out group-hover:opacity-100 -mr-2">
              <Tooltip content={actionLabels["delete"]} placement="top">
                <CoButton
                  variant={ButtonVariant.Flat}
                  size="sm"
                  onClick={() => onDelete(history)}
                  className="text-alert-fg-base"
                  icon="icon-trash-2"
                />
              </Tooltip>
              <Tooltip content={actionLabels["save-to-list"]} placement="top">
                <CoButton
                  variant={ButtonVariant.Flat}
                  size="sm"
                  onClick={() => onSave(history)}
                  icon="icon-bookmark"
                />
              </Tooltip>
            </div>
          </div>
        </div>
        <div
          className={classNames(
            `rounded-2xl absolute top-0 bottom-0 -left-4 -right-4 bg-black opacity-0 transition-opacity ease-in-out group-hover:opacity-[0.015] -z-10`
          )}
        />
      </div>
    );
  }

  return (
    <SidebarNavigationItem
      id={id}
      label={title}
      url={history.link}
      onClick={handleOnClick}
      isSelected={isSelected}
      variant={NavigationTabVariant.Small}
      loading={
        "status" in history &&
        history.status === ThreadInteractionStatus.IN_PROGRESS
      }
      hoverActions={
        !isUploadedPaperItem ? (
          <HistoryActionsButton
            onDelete={() => onDelete(history)}
            onSave={() => onSave(history)}
          />
        ) : null
      }
    />
  );
}

export default HistoryItem;
